import React from 'react'
import { IAveragesStaySharp } from '.'
import DataGrid from '../../../components/DataGrid'
import { Column } from '../../../components/DataGrid/types/types'
import { NA } from '../../../constants'
import { getHumanTime } from '../../../services/utils/date-util'
import { alphanumericCompare } from '../../../services/utils/sort-util'
import { IStudentProgressDetails, ITeacherStoreData } from '../../../stores/teacher.store'
import { ISummaryFilter } from '../SummaryFilters'
import { IStaySharpStats } from "../../../services/utils/staysharp-util"

interface Props {
  selections: ISummaryFilter
  studentsProgress: IStudentProgressDetails[]
  averages: IAveragesStaySharp
}

interface State {
  currentSortField: string
}

let sortMethod = alphanumericCompare
let minSortVal = Number.NEGATIVE_INFINITY
let maxSortVal = Number.POSITIVE_INFINITY
let sortable = true
let columns: Column[] = [
  {
    title: 'FIRST NAME',
    dataProperty: 'studentNameFirst',
    customTooltip: 'Indicates the first name of the student',
    width: '15%',
    sortable,
    sortMethod
  },
  {
    title: 'LAST NAME',
    dataProperty: 'studentNameLast',
    customTooltip: 'Indicates the last name of the student',
    width: '15%',
    sortable,
    sortMethod
  },
  {
    title: 'TOPICS<br/>STARTED',
    dataProperty: 'topicsStarted',
    customTooltip: 'Indicates the topics started',
    sortable,
    sortMethod
  },
  {
    title: 'TOPICS<br/>COMPLETED',
    dataProperty: 'topicsCompleted',
    customTooltip: 'Indicates the topics completed',
    sortable,
    sortMethod
  },
  {
    title: 'QUESTIONS<br/>ANSWERED',
    dataProperty: 'questionsAnswered',
    customTooltip: 'Indicates the questions answered',
    sortable,
    sortMethod
  },
  {
    title: 'ENGLISH<br/>ACCURACY',
    dataProperty: 'completedAccuracyEnglish',
    customTooltip: 'Indicates the completed accuracy in English',
    sortable,
    sortMethod
  },
  {
    title: 'MATH<br/>ACCURACY',
    dataProperty: 'completedAccuracyMath',
    customTooltip: 'Indicates the completed accuracy in Math',
    sortable,
    sortMethod
  },
  // {
  //   title: 'TOTAL ACCURACY',
  //   dataProperty: 'completedAccuracyTotal',
  //   customTooltip: 'Indicates the total accuracy',
  //   sortable,
  //   sortMethod
  // },
  {
    title: 'HINTS ON<br/>INCORRECT',
    dataProperty: 'hintsOnIncorrect',
    customTooltip: 'Indicates the percent of missed questions with hints written',
    sortable,
    sortMethod
  },
  {
    title: ' REVIEW<br/>TIME',
    dataProperty: 'timeOnStaySharp',
    customTooltip: 'Indicates the total study time on stay sharp',
    sortable,
    sortMethod
  },
]

interface ISimplifiedStudentProgress {
    firstName: string
    lastName: string
    emailAddress: string
    isAverage: boolean
    tonp: any[]
    staySharpStats: IStaySharpStats

    averageTopicsStarted: number,
    averageTopicsCompleted: number,
    averageQuestionsAnswered: number,
    averageCompletedAccuracyEnglish: number,
    averageCompletedAccuracyMath: number,
    // averageCompletedAccuracyTotal: number,
    averageHintsOnIncorrect: number,
    averageTimeOnStaySharp: number,
}

let averageStudentProgress = {
    id: "?",
    firstName: "",
    lastName: "",
    emailAddress: "",
    tonp: [],
    isAverage: true,
    averageTopicsStarted: 0,
    averageTopicsCompleted: 0,
    averageQuestionsAnswered: 0,
    averageCompletedAccuracyEnglish: 0,
    averageCompletedAccuracyMath: 0,
    // averageCompletedAccuracyTotal: 0,
    averageHintsOnIncorrect: 0,
    averageTimeOnStaySharp: 0,
}

export default class RosterStaySharpSummary extends React.Component<Props, State> {
  averageUsers = {}

  constructor(props) {
    super(props)
    this.state = {
      currentSortField: "default"
    }
  }

  createContent = (studentProgress: IStudentProgressDetails | ISimplifiedStudentProgress, index: number) => {
    let {emailAddress, firstName, lastName, isAverage, tonp, staySharpStats } = studentProgress
   
    if (!staySharpStats) {
      staySharpStats = {
        emailAddress,
        firstName,
        lastName,
        topicsStarted: -1, 
        topicsCompleted: -1, 
        questionsAnswered: -1, 
        completedAccuracyEnglish: -1, 
        completedAccuracyMath: -1, 
        completedAccuracyTotal: -1, 
        hintsOnIncorrect: -1, 
        timeOnStaySharp: -1,
      }
    }

    let {topicsStarted, topicsCompleted, questionsAnswered, completedAccuracyEnglish, completedAccuracyMath, hintsOnIncorrect, timeOnStaySharp } = staySharpStats

    if (isAverage) {
        let simplifiedProgress = studentProgress as ISimplifiedStudentProgress
        topicsStarted = simplifiedProgress.averageTopicsStarted
        topicsCompleted = simplifiedProgress.averageTopicsCompleted
        questionsAnswered = simplifiedProgress.averageQuestionsAnswered
        completedAccuracyEnglish = simplifiedProgress.averageCompletedAccuracyEnglish
        completedAccuracyMath  = simplifiedProgress.averageCompletedAccuracyMath
        hintsOnIncorrect = simplifiedProgress.averageHintsOnIncorrect
        timeOnStaySharp = simplifiedProgress.averageTimeOnStaySharp
    }

    // Percentages
    let completedAccuracyInfoEnglish = `${Math.round(100*completedAccuracyEnglish)}%`
    let completedAccuracyInfoMath = `${Math.round(100*completedAccuracyMath)}%`
    let hintsOnIncorrectInfo = `${Math.round(100*hintsOnIncorrect)}%`
    let timeOnStaySharpInfo = getHumanTime(timeOnStaySharp, true, true).text
    let id = `roster-lesson-summary-${index}`
    let {averages} = this.props
    let getAverageInfoBasedOnSortField = (currentSortField: string) => {
      let prefix = "Average"
      let averageInfo = ""
      let { averageTopicsStarted, averageTopicsCompleted, averageQuestionsAnswered, averageCompletedAccuracyEnglish, averageCompletedAccuracyMath, averageCompletedAccuracyTotal, averageHintsOnIncorrect, averageTimeOnStaySharp} = averages
      let averageTopicsStartedInfo = Math.round(averageTopicsStarted)
      let averageTopicsCompletedInfo = Math.round(averageTopicsCompleted)
      let averageQuestionsAnsweredInfo = Math.round(averageQuestionsAnswered)
      let averageCompletedAccuracyEnglishInfo = Math.round(100*averageCompletedAccuracyEnglish) + "%"
      let averageCompletedAccuracyMathInfo = Math.round(100*averageCompletedAccuracyMath) + "%"
      let averageHintsOnIncorrectInfo = Math.round(100*averageHintsOnIncorrect) + "%"
      let averageTimeOnStaySharpInfo = getHumanTime(averageTimeOnStaySharp, true, true).text

      switch (currentSortField) {
        case "topicsStarted":
          averageInfo = `<span class="average--val">` + averageTopicsStartedInfo + `</span>` + prefix + ` Topics Started`
          averageInfo = averageTopicsStarted !== -1 ? averageInfo: ""
          break
        case "topicsCompleted":
          averageInfo = `<span class="average--val">` + averageTopicsCompletedInfo + `</span>` + prefix + ` Topics Completed`
          averageInfo = averageTopicsCompleted !== -1 ? averageInfo: ""
          break
        case "questionsAnswered":
          averageInfo = `<span class="average--val">` + averageQuestionsAnsweredInfo + `</span>` + prefix + ` Questions Answered`
          averageInfo = averageQuestionsAnswered !== -1 ? averageInfo: ""
          break
        case "completedAccuracyEnglish":
          averageInfo = `<span class="average--val">` + averageCompletedAccuracyEnglishInfo + `</span>` + prefix + ` Completed English Accuracy`
          averageInfo = averageCompletedAccuracyEnglish !== -1 ? averageInfo: ""
          break
        case "completedAccuracyMath":
          averageInfo = `<span class="average--val">` + averageCompletedAccuracyMathInfo + `</span>` + prefix + ` Completed Math Accuracy`
          averageInfo = averageCompletedAccuracyMath !== -1 ? averageInfo: ""
          break
        case "hintsOnIncorrect":
          averageInfo = `<span class="average--val">` + averageHintsOnIncorrectInfo + `</span>` + prefix + ` Hints on Incorrect`
          averageInfo = averageCompletedAccuracyTotal !== -1 ? averageInfo: ""
          break
        case "timeOnStaySharp":
          averageInfo = `<span class="average--val">` + averageTimeOnStaySharpInfo + `</span>` + prefix + ` Time on Stay Sharp`
          averageInfo = averageCompletedAccuracyTotal !== -1 ? averageInfo: ""
          break
      }
      return averageInfo ? averageInfo : ""
    }
    let {currentSortField} = this.state
    let averageInfo = getAverageInfoBasedOnSortField(currentSortField)
    let getFirstNameOrAverageDetails = () => {
      return () =>
        <>
          { !isAverage ?
            <>
              {firstName}
            </>
            :
            <>
              <span dangerouslySetInnerHTML={{__html: averageInfo}}/>
            </>
          }
        </> 
    }
    let data = {
        studentNameFirst: {
          // We are overloading this with first name or average details (based on the sort field)
          content: getFirstNameOrAverageDetails(),
          comparator: firstName,
        }, 
        studentNameLast: {
          content: lastName,
          comparator: lastName,
        },
        topicsStarted: {
          content: topicsStarted > -1 ? topicsStarted: NA,
          comparator: topicsStarted > -1 ? topicsStarted: minSortVal,
        },
        topicsCompleted: {
            content: topicsCompleted > 0 ? topicsCompleted: NA,
            comparator: topicsCompleted > -1 ? topicsCompleted: minSortVal,
        },
        questionsAnswered: {
          content: questionsAnswered > -1 ? questionsAnswered: NA,
          comparator: questionsAnswered > -1 ? questionsAnswered: minSortVal,
        },
        completedAccuracyEnglish: {
          content: completedAccuracyEnglish > -1 ? completedAccuracyInfoEnglish: NA,
          comparator: completedAccuracyEnglish > -1 ? completedAccuracyEnglish: minSortVal,
        },
        completedAccuracyMath: {
            content: completedAccuracyMath > -1 ? completedAccuracyInfoMath: NA,
            comparator: completedAccuracyMath > -1 ? completedAccuracyMath: minSortVal,
        },
        hintsOnIncorrect: {
          content: hintsOnIncorrect > -1 ? hintsOnIncorrectInfo: NA,
          comparator: hintsOnIncorrect > -1 ? hintsOnIncorrect: minSortVal,
        },
        timeOnStaySharp: {
          content: timeOnStaySharp > -1 ? timeOnStaySharpInfo: NA,
          comparator: timeOnStaySharp > -1 ? timeOnStaySharp: minSortVal,
        },
    }

    // Let's highlight the average row here (please note if there is no averageInfo then collapse the row!)
    let highlightRowClassName  = ""
    if (isAverage) {
      if (averageInfo !== "") {
        highlightRowClassName = "row-highlight"
      }
      else {
        highlightRowClassName = "row-highlight-collapsed"
      }
    }

    return {
      id,
      data,
      highlightRowClassName,
    }
  }
  
  onSortAllowed = (sortFieldName: string) => {
    let {currentSortField} = this.state
    if (sortFieldName !== currentSortField) {
      setTimeout(() => {
        this.setState({currentSortField: sortFieldName})
      }, 10)
    }
    return true
  }

  getCardBodyContents = (studentsProgress: IStudentProgressDetails[]) => {
    let {averages} = this.props
    let extendedStudentsProgress = [...studentsProgress]

    // Add average row data only if there are more than 1 entry
    if (studentsProgress.length > 1) {
      averageStudentProgress.averageTopicsStarted = averages.averageTopicsStarted
      averageStudentProgress.averageTopicsCompleted = averages.averageTopicsCompleted
      averageStudentProgress.averageQuestionsAnswered = averages.averageQuestionsAnswered
      averageStudentProgress.averageCompletedAccuracyEnglish = averages.averageCompletedAccuracyEnglish
      averageStudentProgress.averageCompletedAccuracyMath = averages.averageCompletedAccuracyMath
      averageStudentProgress.averageHintsOnIncorrect = averages.averageHintsOnIncorrect
      averageStudentProgress.averageTimeOnStaySharp = averages.averageTimeOnStaySharp
      extendedStudentsProgress.push(averageStudentProgress as any)
    }

    let data = extendedStudentsProgress.map((progress, index) => this.createContent(progress, index))
  
    return (
      <DataGrid
        className="lesson-table tbl-1-2-fixed col-1-static"
        columns={columns}
        data={data}
        onSortAllowed={this.onSortAllowed}
        emptyText="No stay sharp summary found for students."
      />
    )
  }

  public render() {
    let {studentsProgress} = this.props
    if (!studentsProgress) return null

    let summaryDetails = this.getCardBodyContents(studentsProgress)

    return (
      <div className="roster-view-lesson-summary-container">
        <div className="individual-data-section">
        <h2 className="h3">Stay Sharp Summary</h2>
        </div> 
        <hr/>

        <div className="roster-view-content">
          {summaryDetails}
        </div>
      </div>
    )
  }
}
